// СТРОКИ ЧАРТА — общий дом для паспорта (§IV, §V) и overlay шага 6 (B8-1).
//
// Вся строковая логика шага 6 была написана раньше, но адресована паспорту и заперта в модульных
// константах `SectionIV.jsx` (`ITALIC`, `italicPhrase`) и `SectionV.jsx` (`MECH`, `patternName`).
// Overlay обязан читать тот же узел, не копию — копия расходится молча (`s524.d2`). Подлежащее
// (`SUBJECT`) переехало в `findingBank.js` раньше (B6-2) и реэкспортируется отсюда, чтобы у
// overlay был один адрес на все строки чарта.
//
// ⛔ СТРОКИ ЗДЕСЬ НЕ СОЧИНЯЮТСЯ. Это перенос буква-в-букву; байт-парити §IV (F9) и §V (F5) держит
// его без правки эталонов.
//
// ⛔ `levelBand` ЗДЕСЬ НЕ СЧИТАЕТСЯ. Он приходит в `view` из движка (`levelBand` в
// `src/insights/geometry.js`, `s531.d5`: последняя отвеченная точка, порог 1.5). Борд
// `Life Map - s6 Overlay v10.html` считает свой (среднее, порог 1.0) — это черновик борда, не
// правило, и повторять его нельзя.
//
// ⚠️ АПОСТРОФ — ДВЕ ЗАКОННЫЕ ФОРМЫ (`s553.d1`): U+0027 в §IV паспорта, U+2019 на overlay. Узел
// отдаёт обе явно, литералами, и ничего не нормализует: «починить» одну под другую — значит
// сломать байт-парити одного из двух потребителей.
//
// ⛔ ПЕЧАТНЫХ ИМЁН СЛОЁВ ЗДЕСЬ НЕТ — по той же причине, что в `findingBank.js`: у потребителей они
// разные. `patternName` принимает функцию имени слоя параметром.

import { onePoint } from './findingBank.js';

export { SUBJECT } from './findingBank.js';

// ---- italic под чартом (17/17, plateau расщеплён по levelBand) ----
/** Форма паспорта (§IV): апостроф U+0027. */
export const ITALIC = {
  'monotonic-descent': 'A glide path, still in flight.',
  'monotonic-ascent': 'A long exposure.',
  'plateau:high': 'A high orbit.',
  'plateau:mid': 'Sea level.',
  'plateau:low': 'Below sea level.',
  'peak-then-fall': 'Past the summit.',
  'V-shape': 'To the deep and back.',
  'late-cliff': 'Leaving the long flat.',
  'late-rise': 'above and below, one life.',
  'early-cliff': 'above and below, one life.',
  'early-rise': 'you got there. now what.',
  oscillation: 'the same hills.',
  'double-peak': "twice isn't luck.",
  'double-valley': "that's twice.",
  'way-back': 'The way back.',
  'high-held-collapse': '...and this is my life too',
  irregular: 'all this time, this was living.',
};

/** Форма overlay: отличается от паспорта ровно двумя строками — апостроф U+2019. */
export const ITALIC_OVERLAY = {
  ...ITALIC,
  'double-peak': 'twice isn’t luck.',
  'double-valley': 'that’s twice.',
};

/** Ключ банка: plateau — составной, по `levelBand` из движка. */
export const italicKey = (v) => (v.geometryClass === 'plateau' ? `plateau:${v.levelBand}` : v.geometryClass);

/** Фолбэк курсива при неизвестном классе — один на §IV и шаг 5 (спека шага 5, `s7.italic_phrase`). */
export const ITALIC_FALLBACK = 'the red line.';

/** Строка паспорта (§IV); неизвестный класс → фолбэк. */
// Н41 (`s950.d1`): при одной отвеченной точке курсива нет — «A high orbit.» / «Sea level.» утверждают
// уровень на всей дуге по одной точке. `null`, не пустая строка: потребитель снимает узел.
export const italicPhrase = (v) => (onePoint(v) ? null : ITALIC[italicKey(v)] || ITALIC_FALLBACK);

/** Строка визарда (шаг 5): форма overlay (U+2019), тот же фолбэк. */
export const italicPhraseOverlay = (v) => (onePoint(v) ? null : ITALIC_OVERLAY[italicKey(v)] || ITALIC_FALLBACK);

// ---- кэп правой брови чарта (s394): длина `CARRIERS · …`, выше которой бровь уходит в безымянную
// форму расклада. Один на §IV паспорта и бровь шага 5 (B7-1). ----
export const EYB_CAP = { pair: 42, triplet: 55, split: 48 };

/** Числа словом до потолка этапов (15). Один на §IV паспорта и шаг 5 (B7-3). */
export const NUMW = { 0: 'zero', 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten', 11: 'eleven', 12: 'twelve', 13: 'thirteen', 14: 'fourteen', 15: 'fifteen' };

/**
 * Правая бровь чарта — банк шести форм (`s310.d1`) + правило переполнения (`s394`).
 *
 * ⭐ ПЕРЕЕХАЛА СЮДА ИЗ `SectionIV.jsx` (B7-3) буква в букву: шаг 5 печатает ту же бровь
 * (`carrierEyebrow()` борда v7 `:579–586` — порт §IV целиком), и вторая копия расходилась бы
 * молча. Печатное имя слоя — параметром, как у `patternName`: у §IV `security`, у шага 5
 * `sense of security`.
 *
 * @param {{carrierConfig: string, members: string[], eyebrowMembers?: string[]}} v
 * @param {(key: string) => string} lname печатное имя слоя у потребителя
 * @returns {{label: string, body: string, capped: boolean}}
 */
export function carrierEyebrow(v, lname) {
  const m = v.eyebrowMembers || v.members, u = (k) => lname(k).toUpperCase();
  let s, fallback;
  switch (v.carrierConfig) {
    case 'singleton': return { label: 'carrier', body: u(m[0]), capped: false };
    case 'pair': s = `${u(m[0])} + ${u(m[1])}`; fallback = 'TWO LAYERS TOGETHER'; break;
    case 'triplet': s = `${u(m[0])} + ${u(m[1])} + ${u(m[2])}`; fallback = 'THREE LAYERS TOGETHER'; break;
    case 'field': return { label: 'carriers', body: `${(NUMW[m.length] || m.length).toUpperCase()} LAYERS TOGETHER`, capped: false };
    case 'split': s = `${u(m[0])} AGAINST ${u(m[1])}`; fallback = 'TWO LAYERS, PULLING APART'; break;
    default: return { label: 'carriers', body: 'ALL SEVEN', capped: false };
  }
  const cap = EYB_CAP[v.carrierConfig];
  const over = ('CARRIERS · ' + s).length > cap;
  return { label: 'carriers', body: over ? fallback : s, capped: over };
}

/**
 * Тихая строка на месте цитаты (`t.ev.silent`) — ФОРМА ВИЗАРДА, апостроф U+2019 (`s946.d6`, как в
 * `Red Line v7`). ⛔ Паспорт печатает U+0027 (`SectionIV.jsx`, заморозка `s553.d1`) и отсюда не
 * читает: две формы законны, нормализовать одну под другую — сломать парити одного из двух.
 */
export const EV_SILENT_OVERLAY = 'the line’s shape carries this one.';

// ---- баннер §V · pattern_name ----
export const MECH = { together: ', moving as one', field: ', moving as one', opposition: ' trade-off', singleton: ', on its own', scatter: 'an even spread' };
// раскол = механизм trade-off, не handoff (canon s464.d1; движковый verify CC: split ⟺
// family=opposition, carrier = одна доминантная/усреднённая линия, эстафеты нет). Две формы по
// числу несущих: пара-оппозиция (двое названных) → «{a} and {b}, a trade-off»; group-vs-group
// (блок против блока, несущих >2) → «every layer, a trade-off» — имена верхнего блока не тянутся
// (D-s222-1), ветка ловится по типу карточки group-vs-group. Прежняя безымянная «pulling apart in
// two directions» (s411/8в) в Field Mapping Spec не входила — снята s464.d1. opposition и split —
// пара: opposition — семейство движка (family), split — рендер-ключ (carrierConfig, через SPLIT_W
// ниже); одно не отменяет другое (s865.d2).
export const SPLIT_W = ', a trade-off';
const isGroupSplit = (v) => (v.cards || []).some((c) => c.type === 'group-vs-group');

/** Имя главного паттерна. `lname` — печатное имя слоя у потребителя (у §V: `vitality`, …). */
export function patternName(v, lname) {
  const w = MECH[v.family] || ', moving as one';
  if (v.family === 'scatter') return { t: MECH.scatter, flag: null };
  if (v.carrierConfig === 'split') {
    if (isGroupSplit(v)) return { t: 'every layer' + SPLIT_W, flag: null };
    const s = (v.nameMembers || []).map(lname);
    return { t: `${s[0]} and ${s[1]}${SPLIT_W}`, flag: null };
  }
  const m = (v.nameMembers || []).map(lname);
  let names;
  // ordered longest→shortest; the empty fallback guards against leaking "undefined"
  // (the 3-member branch indexes m[0..2]). Parity boards always feed 1..3 or field/ALL7.
  if (v.carrierConfig === 'field' || m.length > 3) names = 'every layer';
  else if (m.length === 3) names = `${m[0]}, ${m[1]} and ${m[2]}`;
  else if (m.length === 2) names = `${m[0]} and ${m[1]}`;
  else if (m.length === 1) names = m[0];
  else names = 'every layer';
  return { t: names + w, flag: null };
}
